/**
 * @fileoverview Helpers for the post-sign-in destination (`callbackUrl`). The
 * app shell sends signed-out visitors to `/sign-in?callbackUrl=<page>`. GitHub
 * OAuth leaves the app and comes back to `/sign-in?ct=<jwt>` without that query
 * string, so the destination is also kept in sessionStorage for the round trip.
 */

const STORAGE_KEY = 'toolpad_sign_in_callback';

/**
 * Returns `value` when it is a path inside this app, otherwise `/`. Rejects
 * absolute and protocol-relative URLs so a crafted link can't send the user to
 * another site after signing in, and never returns to the sign-in page itself.
 */
export function getSafeCallbackUrl(value: string | null | undefined): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
    return '/';
  }
  if (/^\/sign-in(?:[/?#]|$)/.test(value)) {
    return '/';
  }
  return value;
}

/**
 * Remembers where to go once the GitHub OAuth redirect returns, or forgets any
 * earlier destination when `value` is null.
 */
export function rememberCallbackUrl(value: string | null): void {
  try {
    if (value) {
      sessionStorage.setItem(STORAGE_KEY, getSafeCallbackUrl(value));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage unavailable (e.g. privacy mode): fall back to the home page.
  }
}

/** Returns the destination saved by `rememberCallbackUrl`, if any. */
export function getRememberedCallbackUrl(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
